import { useMemo } from 'react';
import ChapterSection from '../ChapterSection/ChapterSection';
import ChapterMap from '../ChapterMap/ChapterMap';
import type { Point, ReinfoLayerSummary, ReinfoPortfolio } from '@/lib/api.types';
import type { LegendGradient, LegendItem, MapSpec, MarkerSpec } from '@/lib/map-spec';
import type { LeadSpec, RowSpec } from '@/lib/report-content';
import { TILES } from '@/lib/tiles';
import {
  PRICE_HEAT_COLORS,
  priceHeatColor,
} from '@/lib/chapters';
import { formatDistanceM, formatYen, formatYenPerM2, TSUBO_M2 } from '@/lib/format';
import { cullOverlappingLabels } from '@/lib/map-labels';
import {
  ChapterLead,
  CheckList,
  EmptyState,
  RawDetails,
  RowList,
} from './shared';

type Props = {
  portfolio: ReinfoPortfolio;
  site: Point;
};

// 価格マップの固定ズーム。範囲は固定し、この表示範囲に入る価格ポイントは間引かず全て描画する
// （ズームは動的に変えない）。周辺の点をなるべく多く収めるため浸水マップよりやや広域の 14 とする。
// ラベルの間引き (cullOverlappingLabels) はこの固定ズームの画素座標で判定する。
const PRICE_MAP_ZOOM = 14;
const MAP_HEIGHT = 280;

function getNumberMetric(
  metrics: Record<string, unknown>,
  key: string,
): number | null {
  const v = metrics[key];
  return typeof v === 'number' ? v : null;
}

function buildPriceMap(
  txLayer: ReinfoLayerSummary | undefined,
  publicLayer: ReinfoLayerSummary | undefined,
  site: Point,
): MapSpec {
  // 1) 両レイヤから価格ポイントを集める。バックエンドの値は坪単価(円)に正規化されているので、
  //    地図の表示単位である ㎡ 単価へ換算して持つ (吹き出し・凡例・ツールチップは ㎡ 単価。行の中央値は坪単価のまま)。
  //    地図上では取引価格と地価公示を区別せず、どちらも同じ「〇〇万円/㎡」の吹き出しで示す (出どころはツールチップ)。
  type PricePt = { point: Point; perM2: number | null; title: string };
  const collected: PricePt[] = [];

  function collect(layer: ReinfoLayerSummary | undefined, kind: string): void {
    if (!layer) return;
    const geoms = layer.feature_geometries ?? [];
    const labels = layer.feature_labels ?? [];
    const values = layer.feature_price_values ?? [];
    let added = 0;
    geoms.forEach((g, i) => {
      if (g.type !== 'Point') return;
      const coords = g.coordinates as [number, number];
      const v = values[i];
      const perTsubo = typeof v === 'number' && Number.isFinite(v) ? v : null;
      const perM2 = perTsubo != null ? perTsubo / TSUBO_M2 : null;
      // ツールチップ: ㎡ 単価 ／ 出どころ: バックエンドのラベル (取引: 地区名・総額・面積・坪単価・時期、公示: 円/㎡・前年比)。
      const detail = labels[i] ?? layer.value_label ?? (perTsubo != null ? `坪単価 ${formatYen(perTsubo)}` : '');
      const source = detail ? `${kind}: ${detail}` : kind;
      collected.push({
        point: { lon: coords[0], lat: coords[1] },
        perM2,
        title: perM2 != null ? `${formatYenPerM2(perM2)} ／ ${source}` : source,
      });
      added += 1;
    });
    // Fallback: at least show the nearest point if the layer didn't expose Point geometries.
    if (added === 0 && layer.nearest_point) {
      const dist = layer.nearest_distance_m != null ? formatDistanceM(layer.nearest_distance_m) : '';
      collected.push({ point: layer.nearest_point, perM2: null, title: `${kind} ${dist}`.trim() });
    }
  }

  collect(txLayer, '取引価格');
  collect(publicLayer, '地価公示・標準地');

  // 2) 取得した価格ポイントは間引かず全て描画する。地図の表示範囲は固定（PRICE_MAP_ZOOM）で、
  //    その範囲に入る点はすべて表示される（範囲外の点は浸水マップ同様に自然に画面外になる）。
  const pts = collected;

  // 3) 表示範囲内の ㎡ 単価レンジを確定し、各点を寒色(安)→暖色(高)に色分け。各点は ㎡ 単価の吹き出し
  //    (単価の色で塗る) で示し、重なる分は調査地点に近い点を優先して間引く (落ちた点は小さな丸になり、
  //    色とツールチップは残る)。単価が取れない点は吹き出しを持たず中立色の丸。
  const vals = pts.map((p) => p.perM2).filter((v): v is number => v != null);
  const min = vals.length ? Math.min(...vals) : 0;
  const max = vals.length ? Math.max(...vals) : 0;
  const markers: MarkerSpec[] = cullOverlappingLabels(
    pts.map((p) => ({
      point: p.point,
      color: priceHeatColor(p.perM2, min, max),
      label: p.perM2 != null ? formatYenPerM2(p.perM2) : undefined,
      title: p.title,
    })),
    site,
    PRICE_MAP_ZOOM,
  );

  // 4) 凡例：㎡ 単価のカラースケール（グラデーションバー）。取引価格と地価公示は地図上で区別しない。
  let legendGradient: LegendGradient | undefined;
  const legend: LegendItem[] = [];
  if (vals.length >= 2 && max > min) {
    legendGradient = {
      colors: PRICE_HEAT_COLORS,
      minLabel: `安 ${formatYenPerM2(min)}`,
      maxLabel: `${formatYenPerM2(max)} 高`,
    };
  } else if (vals.length >= 1) {
    // 1点のみ / 全点同値：レンジが無いのでグラデーションではなく単一チップで ㎡ 単価を示す。
    legend.push({ color: priceHeatColor(vals[0], min, max), label: formatYenPerM2(vals[0]) });
  }

  return {
    key: 'price',
    point: site,
    tileUrl: TILES.pale,
    zoom: PRICE_MAP_ZOOM,
    height: MAP_HEIGHT,
    geometries: [],
    markers,
    legend,
    legendGradient,
    caption: '周辺の ㎡ 単価ヒートマップ（取引価格・地価公示を合わせた参考値。色は表示範囲内の相対の目安、吹き出しが重なる点は小さな丸）',
    emptyMessage: 'この付近では価格参考ポイントが見つかりませんでした。',
  };
}

export type PriceContent = {
  lead: LeadSpec;
  maps: MapSpec[];
  rows: RowSpec[];
  checkItems: string[];
  emptyText: string | null;
};

// 価格章の中身。画面 (ChapterPrice) と PDF 冊子が共有する純関数。
export function buildPriceContent(portfolio: ReinfoPortfolio, site: Point): PriceContent {
  const txMedian = getNumberMetric(portfolio.metrics, 'median_transaction_price_per_tsubo_yen');
  const publicTsuboMedian = getNumberMetric(portfolio.metrics, 'median_public_price_per_tsubo_yen');
  const publicM2Median = getNumberMetric(portfolio.metrics, 'median_public_price_per_m2_yen');
  const txSamples = getNumberMetric(portfolio.metrics, 'transaction_samples') ?? 0;

  const txLayer = portfolio.layers.find((l) => l.endpoint === 'XPT001');
  const publicLayer = portfolio.layers.find((l) => l.endpoint === 'XPT002');

  // 地価公示・地価調査の対前年変動率（最寄り標準地）。レスポンスに同梱されており追加取得は不要。
  // 「いまの相場」の章として直近の動きまでを扱う（長期トレンドの語りは見通し章に委ねない方針で、地価は本章のみ）。
  const publicYoyRaw = publicLayer?.representative_properties?.['year_on_year_change_rate'];
  const publicYoyNum =
    typeof publicYoyRaw === 'number'
      ? publicYoyRaw
      : typeof publicYoyRaw === 'string'
        ? Number.parseFloat(publicYoyRaw)
        : NaN;
  const publicYoy = Number.isFinite(publicYoyNum) ? publicYoyNum : null;

  const rows: RowSpec[] = [];
  if (txMedian != null) {
    rows.push({
      icon: 'currency-yen',
      label: '取引価格 中央値 (坪単価)',
      detail: `周辺タイル ${txSamples} 件の中央値です`,
      status: 'neutral',
      statusLabel: formatYen(txMedian),
    });
  }
  if (publicTsuboMedian != null) {
    rows.push({
      icon: 'building-bank',
      label: '地価公示・地価調査 中央値 (坪単価)',
      detail: publicM2Median != null ? `㎡ 単価: ${formatYen(publicM2Median)}` : undefined,
      status: 'neutral',
      statusLabel: formatYen(publicTsuboMedian),
    });
  }
  if (publicYoy != null) {
    rows.push({
      icon: 'chart-line',
      label: '地価の前年比（最寄り標準地）',
      detail: '地価公示・地価調査の対前年変動率です',
      status: 'neutral',
      statusLabel: `${publicYoy > 0 ? '+' : ''}${publicYoy.toFixed(1)}%`,
    });
  }
  if (txLayer?.nearest_distance_m != null) {
    rows.push({
      icon: 'map-pin',
      iconTone: 'inherit',
      label: '最寄り取引ポイント',
      detail: formatDistanceM(txLayer.nearest_distance_m),
      status: 'neutral',
      statusLabel: '参考',
    });
  }
  if (publicLayer?.nearest_distance_m != null) {
    rows.push({
      icon: 'map-pin',
      iconTone: 'inherit',
      label: '最寄り標準地',
      detail: formatDistanceM(publicLayer.nearest_distance_m),
      status: 'neutral',
      statusLabel: '参考',
    });
  }

  return {
    lead: { label: portfolio.label, message: portfolio.buyer_message },
    maps: [buildPriceMap(txLayer, publicLayer, site)],
    rows,
    checkItems: portfolio.check_items,
    emptyText:
      portfolio.layers.length === 0
        ? '周辺の価格データを取得できませんでした。仲介資料や近隣事例で確認してください。'
        : null,
  };
}

export default function ChapterPrice({ portfolio, site }: Props) {
  const content = useMemo(() => buildPriceContent(portfolio, site), [portfolio, site]);

  return (
    <ChapterSection id="price">
      <ChapterLead {...content.lead} />

      {content.maps.map((spec) => (
        <ChapterMap key={spec.key} spec={spec} />
      ))}

      {content.emptyText ? (
        <EmptyState>{content.emptyText}</EmptyState>
      ) : (
        <RowList rows={content.rows} />
      )}

      <CheckList items={content.checkItems} />
      <RawDetails portfolio={portfolio} />
    </ChapterSection>
  );
}
